import { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AssistantChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Merhaba! Ben AI Asistan Gülce. Ürünlerimiz, fiyatlar, stok veya kargo hakkında sorularınızı yanıtlayabilirim." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("ata_assistant_session") || "");
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("stream unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          let payload;
          try { payload = JSON.parse(part.slice(6)); } catch { continue; }
          if (payload.delta) {
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + payload.delta };
              return next;
            });
          }
          if (payload.error) {
            setMessages((m) => { const next = [...m]; next[next.length - 1] = { role: "assistant", content: payload.error }; return next; });
          }
          if (payload.session_id) {
            setSessionId(payload.session_id);
            localStorage.setItem("ata_assistant_session", payload.session_id);
          }
        }
      }
    } catch {
      setMessages((m) => { const next = [...m]; next[next.length - 1] = { role: "assistant", content: "Bağlantı hatası, lütfen tekrar deneyin." }; return next; });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="assistant-open-button"
        onClick={() => setOpen(!open)}
        aria-label="Yapay zeka asistanı"
        className="fixed bottom-20 md:bottom-6 left-4 z-40 h-14 px-5 rounded-full bg-forest hover:bg-forest-dark text-white shadow-lg shadow-green-900/30 flex items-center gap-2 hover:scale-105 transition-transform"
      >
        <Sparkles className="w-5 h-5 text-gold-light" />
        <span className="text-sm font-semibold hidden sm:inline">AI Asistan Gülce</span>
      </button>
      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-36 md:bottom-24 left-4 z-40 w-[calc(100vw-2rem)] max-w-sm bg-white rounded-2xl border border-stone-200 shadow-2xl flex flex-col overflow-hidden" style={{ height: "28rem" }}>
          <div className="bg-forest text-cream px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-gold-light" />
              <div><p className="text-sm font-semibold">AI Asistan Gülce</p><p className="text-[10px] text-cream/60">Yapay zeka ile ürün danışmanı</p></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="assistant-close" aria-label="Kapat" className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
          </div>
          <div data-testid="assistant-messages" className="flex-1 overflow-y-auto p-4 space-y-3 bg-cream/50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-forest text-white rounded-br-sm" : "bg-white border border-stone-200 text-stone-800 rounded-bl-sm"}`}>
                  {m.content || (busy && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-forest" /> : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="p-3 border-t border-stone-200 bg-white flex gap-2">
            <input
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Örn: Bal fiyatları nedir?"
              className="flex-1 h-10 px-3 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30"
            />
            <button data-testid="assistant-send" disabled={busy} className="h-10 w-10 rounded-xl bg-forest hover:bg-forest-dark disabled:bg-stone-300 text-white flex items-center justify-center transition-colors" aria-label="Gönder">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
